/* InvoiceFormDialog — create / edit an invoice scoped to a patient.
 *
 * iter98.99y rebuild:
 *  • Slide-over right-side drawer (Sheet) instead of centered modal —
 *    matches the Stripe / Linear / Notion pattern. Wider canvas, room
 *    to surround the form with patient context without crowding.
 *  • Doctor-curated **service catalog** combobox next to "Add line
 *    item" — 1-click inserts pre-priced services (Consultation,
 *    Refinement, Retainer, …). Manage panel lets the doctor add /
 *    edit / archive items inline.
 *  • Patient context sidebar (right column) — surface last 3 invoices,
 *    total outstanding, and linked active cases so the doctor decides
 *    with informed context, not in a vacuum.
 *
 * Required: { patientId, patientName }.
 * Optional: { cases } (drives the case picker), { defaultCaseId } (pre-
 *   selects one), { existingInvoice } (switches to edit mode).
 */
import React, { useEffect, useState, useMemo, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
    Plus, Trash, Receipt, BookmarkSimple, CaretDown, PencilSimple, X, FloppyDisk,
    Tag, ClockClockwise,
} from "@phosphor-icons/react";

const DEFAULT_TAX_PCT = 18;
const DEFAULT_DUE_DAYS = 7;

/* Convenience: today + N days as ISO YYYY-MM-DD (invoice due dates live
 * as date-only strings on the server). Smart default so the doctor
 * doesn't have to reach for the calendar for the 95% case. */
function todayPlusDaysISO(days) {
    const d = new Date();
    d.setDate(d.getDate() + (days || 0));
    return d.toISOString().slice(0, 10);
}

function formatINR(amount) {
    if (amount === null || amount === undefined) return "—";
    return new Intl.NumberFormat("en-IN", {
        style: "currency", currency: "INR", maximumFractionDigits: 2,
    }).format(amount);
}

const LINE_TYPES = [
    { value: "service", label: "Service" },
    { value: "case", label: "Case / kit" },
    { value: "appointment", label: "Appointment" },
    { value: "adjustment", label: "Adjustment" },
];

const STATUS_TONE = {
    /* iter98.99ad — Draft + Sent both render as "Unpaid" to the doctor.
     * Draft creation retired; legacy drafts auto-migrated on backend
     * list-read. */
    draft:         "bg-primary/10 text-primary",
    sent:          "bg-primary/10 text-primary",
    viewed:        "bg-sky-50 text-sky-700",
    partial:       "bg-amber-50 text-amber-800",
    paid:          "bg-emerald-50 text-emerald-800",
    refunded:      "bg-violet-50 text-violet-700",
    uncollectible: "bg-slate-100 text-slate-700",
    void:          "bg-rose-50 text-rose-700",
};

const STATUS_LABEL = {
    draft: "Unpaid",
    sent: "Unpaid",
    viewed: "Viewed",
    partial: "Partly paid",
    paid: "Paid",
    refunded: "Refunded",
    uncollectible: "Uncollectible",
    void: "Void",
};

// Stable client-side keys so React doesn't reuse rows when items shift.
let _itemKeySeq = 0;
const newKey = () => `li-${++_itemKeySeq}`;


export default function InvoiceFormDialog({
    open, onClose, onSaved,
    patientId, patientName,
    cases = [],
    defaultCaseId = null,
    existingInvoice = null,
}) {
    const isEdit = !!existingInvoice;
    const [caseId, setCaseId] = useState(defaultCaseId || "");
    const [taxPct, setTaxPct] = useState(DEFAULT_TAX_PCT);
    const [dueAt, setDueAt] = useState("");
    const [notes, setNotes] = useState("");
    const [items, setItems] = useState([
        { _key: newKey(), description: "", line_type: "service", quantity: 1, unit_price: "" },
    ]);
    const [submitting, setSubmitting] = useState(false);

    // Catalog state — fetched once when the drawer opens.
    const [catalog, setCatalog] = useState([]);
    const [showCatalog, setShowCatalog] = useState(false);
    const [showCatalogManage, setShowCatalogManage] = useState(false);

    // Patient context (sidebar) — also fetched on open.
    const [pastInvoices, setPastInvoices] = useState([]);
    const [contextLoading, setContextLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        if (existingInvoice) {
            setCaseId(existingInvoice.case_id || "");
            setTaxPct(existingInvoice.tax_pct ?? DEFAULT_TAX_PCT);
            setDueAt(existingInvoice.due_at || "");
            setNotes(existingInvoice.notes || "");
            setItems(
                (existingInvoice.line_items || []).map((li) => ({
                    _key: newKey(),
                    description: li.description || "",
                    line_type: li.line_type || "service",
                    quantity: li.quantity || 1,
                    unit_price: li.unit_price ?? "",
                })),
            );
        } else {
            setCaseId(defaultCaseId || "");
            setTaxPct(DEFAULT_TAX_PCT);
            /* iter98.99ac — Smart default: due in a week. Doctor can
             * clear or change but doesn't need to reach for the calendar
             * for the common walk-in consult case. */
            setDueAt(todayPlusDaysISO(DEFAULT_DUE_DAYS));
            setNotes("");
            setItems([{ _key: newKey(), description: "", line_type: "service", quantity: 1, unit_price: "" }]);
        }
        setShowCatalog(false);
        setShowCatalogManage(false);
        loadCatalog();
        loadPatientContext();
    }, [open, existingInvoice, defaultCaseId]);

    const loadCatalog = async () => {
        try {
            const r = await api.get("/doctor/service-catalog");
            setCatalog(r.data || []);
        } catch {
            // Silent — catalog is optional.
        }
    };

    const loadPatientContext = async () => {
        if (!patientId) return;
        setContextLoading(true);
        try {
            const r = await api.get(`/doctor/invoices?patient_id=${patientId}`);
            // Exclude the current invoice (edit mode) so the sidebar shows context, not self.
            const list = (r.data || []).filter((i) => !existingInvoice || i.id !== existingInvoice.id);
            list.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
            setPastInvoices(list);
        } catch {
            setPastInvoices([]);
        } finally {
            setContextLoading(false);
        }
    };

    const totals = useMemo(() => {
        const subtotal = items.reduce((s, li) => {
            const q = parseFloat(li.quantity) || 0;
            const u = parseFloat(li.unit_price) || 0;
            return s + q * u;
        }, 0);
        const taxAmount = Math.round(subtotal * (parseFloat(taxPct) || 0)) / 100;
        return { subtotal, taxAmount, total: subtotal + taxAmount };
    }, [items, taxPct]);

    const updateItem = (idx, patch) => {
        setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
    };
    const addItem = () => setItems((prev) => [...prev, { _key: newKey(), description: "", line_type: "service", quantity: 1, unit_price: "" }]);
    const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

    /* Insert a catalog item as a new line. If the bottom line is empty
       (initial blank row), we replace it; otherwise append — feels more
       natural than always appending. */
    const insertCatalogItem = (item) => {
        const newRow = {
            _key: newKey(),
            description: item.label,
            line_type: item.line_type || "service",
            quantity: item.default_quantity || 1,
            unit_price: item.default_price,
        };
        setItems((prev) => {
            const last = prev[prev.length - 1];
            const lastIsEmpty = last && !(last.description || "").trim() && !last.unit_price;
            return lastIsEmpty ? [...prev.slice(0, -1), newRow] : [...prev, newRow];
        });
        setShowCatalog(false);
    };

    const isValid = items.length > 0 && items.every((li) =>
        (li.description || "").trim() && parseFloat(li.unit_price) >= 0 && parseFloat(li.quantity) > 0
    );

    const save = async () => {
        if (!isValid) return;
        setSubmitting(true);
        try {
            const payload = {
                patient_id: patientId,
                case_id: caseId || null,
                currency: "INR",
                tax_pct: parseFloat(taxPct) || 0,
                due_at: dueAt || null,
                notes: notes.trim(),
                line_items: items.map((li) => ({
                    description: li.description.trim(),
                    line_type: li.line_type,
                    reference_id: caseId || null,
                    quantity: parseFloat(li.quantity) || 1,
                    unit_price: parseFloat(li.unit_price) || 0,
                })),
            };
            if (isEdit) {
                await api.put(`/doctor/invoices/${existingInvoice.id}`, payload);
                toast.success("Invoice updated");
            } else {
                await api.post("/doctor/invoices", payload);
                toast.success("Invoice created");
            }
            onSaved && onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to save invoice");
        } finally {
            setSubmitting(false);
        }
    };

    // Patient context derivations.
    const totalOutstanding = useMemo(
        () => pastInvoices
            .filter((i) => !["void", "uncollectible", "refunded"].includes(i.status))
            .reduce((s, i) => s + (i.balance || 0), 0),
        [pastInvoices],
    );
    const recentInvoices = pastInvoices.slice(0, 3);

    return (
        <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
            <SheetContent
                side="right"
                className="!max-w-none w-full sm:w-[92vw] md:w-[840px] lg:w-[1000px] p-0 flex flex-col"
                data-testid="invoice-form-dialog"
            >
                <SheetHeader className="px-6 pt-6 pb-3 border-b border-border">
                    <SheetTitle className="flex items-center gap-2">
                        <Receipt size={18} weight="duotone" className="text-primary" />
                        {isEdit ? `Edit invoice ${existingInvoice?.code || ""}` : "Create invoice"}
                    </SheetTitle>
                    <SheetDescription>
                        For <span className="font-medium text-foreground">{patientName || "patient"}</span>
                    </SheetDescription>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto px-6 py-5">
                    <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6">
                        {/* ───── LEFT : the form ───── */}
                        <div className="space-y-5 min-w-0">
                            {/* Case picker */}
                            {cases.length > 0 && (
                                <div>
                                    <Label htmlFor="case-link" className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Link to case (optional)</Label>
                                    <select
                                        id="case-link"
                                        value={caseId}
                                        onChange={(e) => setCaseId(e.target.value)}
                                        className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                                        data-testid="invoice-case-link"
                                    >
                                        <option value="">No case (general invoice)</option>
                                        {cases.map((c) => (
                                            <option key={c.id} value={c.id}>
                                                {c.case_code || c.code || c.id.slice(0, 8)} · {c.diagnosis || c.status || "—"}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* LINE ITEMS */}
                            <div className="space-y-3">
                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                    <Label className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Line items</Label>
                                    <div className="flex items-center gap-1.5 relative">
                                        {/* iter98.99y — Catalog combobox: insert from saved services */}
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="h-7 text-[11px] rounded-full"
                                            onClick={() => { setShowCatalog((v) => !v); setShowCatalogManage(false); }}
                                            data-testid="invoice-catalog-toggle"
                                        >
                                            <BookmarkSimple size={11} weight="duotone" className="mr-1" />
                                            Insert from services
                                            <CaretDown size={9} weight="bold" className="ml-1 opacity-60" />
                                        </Button>
                                        {showCatalog && (
                                            <CatalogPopover
                                                items={catalog}
                                                onPick={insertCatalogItem}
                                                onManage={() => { setShowCatalog(false); setShowCatalogManage(true); }}
                                                onClose={() => setShowCatalog(false)}
                                            />
                                        )}
                                    </div>
                                </div>
                                {items.map((li, idx) => (
                                    <div key={li._key} className="grid grid-cols-[1fr_110px_70px_110px_auto] gap-2 items-center" data-testid={`invoice-line-${idx}`}>
                                        <Input
                                            placeholder="Description"
                                            value={li.description}
                                            onChange={(e) => updateItem(idx, { description: e.target.value })}
                                            data-testid={`invoice-line-${idx}-description`}
                                        />
                                        <select
                                            value={li.line_type}
                                            onChange={(e) => updateItem(idx, { line_type: e.target.value })}
                                            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                                            data-testid={`invoice-line-${idx}-type`}
                                        >
                                            {LINE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                        </select>
                                        <Input
                                            type="number" min="0" step="0.01"
                                            placeholder="Qty"
                                            value={li.quantity}
                                            onChange={(e) => updateItem(idx, { quantity: e.target.value })}
                                            className="text-right"
                                            data-testid={`invoice-line-${idx}-qty`}
                                        />
                                        <Input
                                            type="number" min="0" step="0.01"
                                            placeholder="Unit price"
                                            value={li.unit_price}
                                            onChange={(e) => updateItem(idx, { unit_price: e.target.value })}
                                            className="text-right tabular-nums"
                                            data-testid={`invoice-line-${idx}-price`}
                                        />
                                        <Button
                                            variant="ghost" size="icon"
                                            onClick={() => removeItem(idx)}
                                            disabled={items.length === 1}
                                            className="h-8 w-8 text-muted-foreground hover:text-rose-600"
                                            data-testid={`invoice-line-${idx}-remove`}
                                        >
                                            <Trash size={14} weight="duotone" />
                                        </Button>
                                    </div>
                                ))}
                                <Button variant="outline" size="sm" onClick={addItem} className="rounded-full" data-testid="invoice-add-line">
                                    <Plus size={12} weight="bold" className="mr-1" /> Add line item
                                </Button>
                            </div>

                            {/* TAX + DUE */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label htmlFor="tax-pct" className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Tax %</Label>
                                    <Input id="tax-pct" type="number" min="0" max="100" step="0.01"
                                        value={taxPct} onChange={(e) => setTaxPct(e.target.value)}
                                        className="mt-1" data-testid="invoice-tax-pct" />
                                </div>
                                <div>
                                    <Label htmlFor="due-at" className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Due date</Label>
                                    <Input id="due-at" type="date"
                                        value={dueAt} onChange={(e) => setDueAt(e.target.value)}
                                        className="mt-1" data-testid="invoice-due-at" />
                                </div>
                            </div>

                            {/* NOTES */}
                            <div>
                                <Label htmlFor="invoice-notes" className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Notes for the patient</Label>
                                <Textarea id="invoice-notes" rows={2}
                                    value={notes} onChange={(e) => setNotes(e.target.value)}
                                    placeholder="Payment terms, instructions…"
                                    className="mt-1 text-sm" data-testid="invoice-notes" />
                            </div>

                            {/* PREVIEW TOTALS */}
                            <div className="rounded-xl bg-secondary/40 border border-border p-4 space-y-1.5 text-sm" data-testid="invoice-totals-preview">
                                <div className="flex justify-between text-muted-foreground">
                                    <span>Subtotal</span>
                                    <span className="tabular-nums">{formatINR(totals.subtotal)}</span>
                                </div>
                                <div className="flex justify-between text-muted-foreground">
                                    <span>Tax ({taxPct}%)</span>
                                    <span className="tabular-nums">{formatINR(totals.taxAmount)}</span>
                                </div>
                                <div className="flex justify-between font-display text-lg font-medium pt-2 mt-2 border-t border-border">
                                    <span>Total</span>
                                    <span className="tabular-nums">{formatINR(totals.total)}</span>
                                </div>
                            </div>
                        </div>

                        {/* ───── RIGHT : patient context sidebar ───── */}
                        <PatientContextSidebar
                            patientName={patientName}
                            totalOutstanding={totalOutstanding}
                            recentInvoices={recentInvoices}
                            allInvoicesCount={pastInvoices.length}
                            cases={cases}
                            currentCaseId={caseId}
                            loading={contextLoading}
                        />
                    </div>
                </div>

                {/* Sticky footer */}
                <div className="border-t border-border px-6 py-3 flex items-center justify-end gap-2 bg-background">
                    <Button variant="outline" onClick={onClose} className="rounded-full" data-testid="invoice-form-cancel">Cancel</Button>
                    <Button onClick={save} disabled={!isValid || submitting} className="rounded-full" data-testid="invoice-form-save">
                        {submitting ? "Saving…" : (isEdit ? "Save changes" : "Create invoice")}
                    </Button>
                </div>
            </SheetContent>

            {/* Catalog management modal — sibling so it doesn't unmount on close */}
            <CatalogManageModal
                open={showCatalogManage}
                catalog={catalog}
                onClose={() => setShowCatalogManage(false)}
                onChanged={loadCatalog}
            />
        </Sheet>
    );
}


/* ─────────── Catalog popover (insert-from-saved) ─────────── */
function CatalogPopover({ items, onPick, onManage, onClose }) {
    const ref = useRef(null);
    useEffect(() => {
        const onDocClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) onClose();
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [onClose]);
    return (
        <div
            ref={ref}
            className="absolute top-full mt-1 right-0 z-40 w-72 rounded-xl border border-border bg-popover shadow-lg p-2"
            data-testid="invoice-catalog-popover"
        >
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground px-2 py-1.5">
                Insert from your services
            </div>
            <div className="max-h-64 overflow-y-auto -mx-1">
                {items.length === 0 ? (
                    <div className="text-[11px] text-muted-foreground px-3 py-4 text-center">
                        No saved services yet. Click <span className="font-semibold">Manage</span> below to add some.
                    </div>
                ) : (
                    items.map((it) => (
                        <button
                            key={it.id}
                            type="button"
                            onClick={() => onPick(it)}
                            className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md hover:bg-secondary/60 text-left transition-colors"
                            data-testid={`catalog-item-${it.id}`}
                        >
                            <div className="flex items-center gap-2 min-w-0">
                                <Tag size={11} weight="duotone" className="text-primary shrink-0" />
                                <span className="text-[12.5px] truncate">{it.label}</span>
                            </div>
                            <span className="text-[11px] tabular-nums text-muted-foreground shrink-0">
                                ₹{Number(it.default_price).toLocaleString("en-IN")}
                            </span>
                        </button>
                    ))
                )}
            </div>
            <div className="border-t border-border mt-1 pt-1.5">
                <button
                    type="button"
                    onClick={onManage}
                    className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded-md hover:bg-secondary/60 text-[11.5px] text-primary"
                    data-testid="invoice-catalog-manage"
                >
                    <PencilSimple size={11} weight="duotone" /> Manage services
                </button>
            </div>
        </div>
    );
}


/* ─────────── Catalog management modal (inline CRUD) ─────────── */
function CatalogManageModal({ open, catalog, onClose, onChanged }) {
    const [items, setItems] = useState([]);
    const [working, setWorking] = useState(false);
    const [draft, setDraft] = useState({ label: "", line_type: "service", default_price: "" });

    useEffect(() => {
        if (open) setItems(catalog || []);
    }, [open, catalog]);

    if (!open) return null;

    const addNew = async () => {
        const label = (draft.label || "").trim();
        const price = parseFloat(draft.default_price);
        if (!label || isNaN(price) || price < 0) {
            toast.error("Give the service a name and a non-negative price");
            return;
        }
        setWorking(true);
        try {
            const r = await api.post("/doctor/service-catalog", {
                label,
                line_type: draft.line_type || "service",
                default_price: price,
                sort_order: (items[items.length - 1]?.sort_order || 0) + 1,
            });
            setItems((prev) => [...prev, r.data]);
            setDraft({ label: "", line_type: "service", default_price: "" });
            onChanged && onChanged();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to add service");
        } finally {
            setWorking(false);
        }
    };

    const archive = async (id) => {
        setWorking(true);
        try {
            await api.delete(`/doctor/service-catalog/${id}`);
            setItems((prev) => prev.filter((i) => i.id !== id));
            onChanged && onChanged();
        } catch {
            toast.error("Couldn't remove service");
        } finally {
            setWorking(false);
        }
    };

    const updatePrice = async (id, newPrice) => {
        const price = parseFloat(newPrice);
        if (isNaN(price) || price < 0) return;
        try {
            const r = await api.put(`/doctor/service-catalog/${id}`, { default_price: price });
            setItems((prev) => prev.map((i) => (i.id === id ? r.data : i)));
            onChanged && onChanged();
        } catch {
            toast.error("Couldn't update price");
        }
    };

    return (
        <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
            <SheetContent
                side="right"
                className="!max-w-none w-full sm:w-[460px] p-0 flex flex-col"
                data-testid="invoice-catalog-manage-dialog"
            >
                <SheetHeader className="px-6 pt-6 pb-3 border-b border-border">
                    <SheetTitle className="flex items-center gap-2">
                        <BookmarkSimple size={18} weight="duotone" className="text-primary" />
                        Manage services
                    </SheetTitle>
                    <SheetDescription>
                        Your saved services — 1-click insert from any invoice form.
                    </SheetDescription>
                </SheetHeader>
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
                    {items.length === 0 && (
                        <div className="text-xs text-muted-foreground text-center py-6">
                            No services yet. Add your first one below.
                        </div>
                    )}
                    {items.map((it) => (
                        <div key={it.id} className="flex items-center gap-2 p-2.5 rounded-lg border border-border bg-secondary/30">
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium truncate">{it.label}</div>
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
                                    {it.line_type}
                                </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                                <span className="text-[11px] text-muted-foreground">₹</span>
                                <Input
                                    type="number" min="0" step="0.01"
                                    defaultValue={it.default_price}
                                    onBlur={(e) => {
                                        if (parseFloat(e.target.value) !== it.default_price) {
                                            updatePrice(it.id, e.target.value);
                                        }
                                    }}
                                    className="h-8 w-24 text-right tabular-nums"
                                    data-testid={`catalog-manage-price-${it.id}`}
                                />
                                <Button
                                    variant="ghost" size="icon"
                                    onClick={() => archive(it.id)}
                                    disabled={working}
                                    className="h-7 w-7 text-muted-foreground hover:text-rose-600"
                                    data-testid={`catalog-manage-archive-${it.id}`}
                                    title="Remove from catalog"
                                >
                                    <Trash size={13} weight="duotone" />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
                {/* Add-new row docked at the bottom */}
                <div className="border-t border-border px-6 py-3 space-y-2">
                    <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Add a service</div>
                    <div className="grid grid-cols-[1fr_110px_auto] gap-2">
                        <Input
                            placeholder="Service name"
                            value={draft.label}
                            onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                            data-testid="catalog-manage-new-label"
                        />
                        <Input
                            type="number" min="0" step="0.01"
                            placeholder="Price"
                            value={draft.default_price}
                            onChange={(e) => setDraft({ ...draft, default_price: e.target.value })}
                            className="text-right tabular-nums"
                            data-testid="catalog-manage-new-price"
                        />
                        <Button
                            onClick={addNew}
                            disabled={working}
                            className="rounded-full"
                            data-testid="catalog-manage-new-save"
                        >
                            <Plus size={12} weight="bold" className="mr-1" />
                            Add
                        </Button>
                    </div>
                    <select
                        value={draft.line_type}
                        onChange={(e) => setDraft({ ...draft, line_type: e.target.value })}
                        className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
                        data-testid="catalog-manage-new-type"
                    >
                        {LINE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                </div>
                <div className="border-t border-border px-6 py-3 flex justify-end">
                    <Button variant="outline" onClick={onClose} className="rounded-full">
                        Done
                    </Button>
                </div>
            </SheetContent>
        </Sheet>
    );
}


/* ─────────── Patient context sidebar ─────────── */
function PatientContextSidebar({
    patientName, totalOutstanding, recentInvoices, allInvoicesCount,
    cases, currentCaseId, loading,
}) {
    const linkedCase = (cases || []).find((c) => c.id === currentCaseId);
    const activeCases = (cases || []).filter((c) => c.id !== currentCaseId).slice(0, 2);
    return (
        <aside className="space-y-4 lg:sticky lg:top-2" data-testid="invoice-patient-sidebar">
            <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                {patientName || "Patient"} · context
            </div>

            {/* Outstanding */}
            <div className="rounded-xl border border-border bg-secondary/30 p-3" data-testid="patient-sidebar-outstanding">
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Total outstanding</div>
                <div className={`mt-1 font-display text-2xl font-bold tabular-nums ${totalOutstanding > 0 ? "text-rose-700" : "text-emerald-700"}`}>
                    {totalOutstanding > 0 ? formatINR(totalOutstanding) : "All settled"}
                </div>
                <div className="text-[10px] text-muted-foreground mt-0.5">
                    Across {allInvoicesCount || 0} prior invoice{allInvoicesCount === 1 ? "" : "s"}
                </div>
            </div>

            {/* Recent invoices */}
            <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1">
                    <ClockClockwise size={11} weight="duotone" /> Recent invoices
                </div>
                {loading ? (
                    <div className="text-[11px] text-muted-foreground py-2">Loading…</div>
                ) : recentInvoices.length === 0 ? (
                    <div className="text-[11px] text-muted-foreground py-2 italic">
                        First invoice for this patient.
                    </div>
                ) : (
                    <div className="space-y-1.5" data-testid="patient-sidebar-recent">
                        {recentInvoices.map((inv) => (
                            <div key={inv.id} className="rounded-lg border border-border bg-background px-2.5 py-1.5">
                                <div className="flex items-center justify-between gap-1.5">
                                    <div className="text-[11px] font-medium font-mono truncate">{inv.code}</div>
                                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[8.5px] font-bold uppercase tracking-wider ${STATUS_TONE[inv.status] || STATUS_TONE.sent}`}>
                                        {STATUS_LABEL[inv.status] || inv.status}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between mt-0.5">
                                    <span className="text-[10px] text-muted-foreground">
                                        {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : "—"}
                                    </span>
                                    <span className="text-[11px] tabular-nums font-medium">
                                        {formatINR(inv.total)}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Linked case + other open cases */}
            {(linkedCase || activeCases.length > 0) && (
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                        Cases
                    </div>
                    {linkedCase && (
                        <div className="rounded-lg border border-primary/30 bg-primary/5 px-2.5 py-1.5 mb-1.5">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-primary mb-0.5">Linked</div>
                            <div className="text-[11.5px] font-medium font-mono truncate">{linkedCase.case_code || linkedCase.code || linkedCase.id.slice(0, 8)}</div>
                            <div className="text-[10px] text-muted-foreground truncate">{linkedCase.diagnosis || linkedCase.status || ""}</div>
                        </div>
                    )}
                    {activeCases.map((c) => (
                        <div key={c.id} className="rounded-lg border border-border bg-background px-2.5 py-1.5 mb-1.5">
                            <div className="text-[11px] font-medium font-mono truncate">{c.case_code || c.code || c.id.slice(0, 8)}</div>
                            <div className="text-[10px] text-muted-foreground truncate">{c.diagnosis || c.status || ""}</div>
                        </div>
                    ))}
                </div>
            )}
        </aside>
    );
}
